import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';

const MCQSection = ({ userProgress }) => {
  const [selectedCategory, setSelectedCategory] = useState('fundamentals');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [showExplanation, setShowExplanation] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(300); // 5 minutes
  const [isTimerActive, setIsTimerActive] = useState(false);

  const categories = [
    {
      id: 'fundamentals',
      name: 'Testing Fundamentals',
      icon: '📚',
      questions: 45,
      difficulty: 'Beginner',
      color: 'green'
    },
    {
      id: 'automation',
      name: 'Automation Frameworks',
      icon: '🤖',
      questions: 62,
      difficulty: 'Intermediate',
      color: 'blue'
    },
    {
      id: 'performance',
      name: 'Performance Testing',
      icon: '⚡',
      questions: 38,
      difficulty: 'Advanced',
      color: 'yellow'
    },
    {
      id: 'security',
      name: 'Security Testing',
      icon: '🔒',
      questions: 29,
      difficulty: 'Advanced',
      color: 'red'
    },
    {
      id: 'scenarios',
      name: 'Scenario-Based',
      icon: '🎯',
      questions: 41,
      difficulty: 'Expert',
      color: 'purple'
    }
  ];

  const sampleQuestions = [
    {
      id: 1,
      category: 'fundamentals',
      question: 'What is the primary purpose of regression testing?',
      options: [
        'To test new functionality',
        'To verify that existing functionality works after changes',
        'To test user interface elements',
        'To measure application performance'
      ],
      correctAnswer: 1,
      explanation: 'Regression testing ensures that previously working functionality continues to work correctly after code changes, new features, or bug fixes.',
      difficulty: 'Easy'
    },
    {
      id: 2,
      category: 'automation',
      question: 'Which design pattern is commonly used in Selenium automation frameworks?',
      options: [
        'Singleton Pattern',
        'Page Object Model',
        'Factory Pattern',
        'Observer Pattern'
      ],
      correctAnswer: 1,
      explanation: 'Page Object Model (POM) is the most widely used design pattern in Selenium automation, helping to create maintainable and reusable code by separating page elements from test logic.',
      difficulty: 'Medium'
    }
  ];

  useEffect(() => {
    let interval = null;
    if (isTimerActive && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining((time) => time - 1);
      }, 1000);
    } else if (timeRemaining === 0) {
      setIsTimerActive(false);
      // Auto-submit when time runs out
    }
    return () => clearInterval(interval);
  }, [isTimerActive, timeRemaining]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins?.toString()?.padStart(2, '0')}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const startTimedPractice = () => {
    setIsTimerActive(true);
    setTimeRemaining(300);
    setCurrentQuestion(0);
    setSelectedAnswer('');
    setShowExplanation(false);
  };

  const handleAnswerSelect = (answerIndex) => {
    setSelectedAnswer(answerIndex);
  };

  const handleSubmitAnswer = () => {
    setShowExplanation(true);
  };

  const handleNextQuestion = () => {
    setCurrentQuestion((prev) => prev + 1);
    setSelectedAnswer('');
    setShowExplanation(false);
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'text-green-700 bg-green-100 border border-green-300';
      case 'Intermediate': return 'text-blue-700 bg-blue-100 border border-blue-300';
      case 'Advanced': return 'text-orange-700 bg-orange-100 border border-orange-300';
      case 'Expert': return 'text-purple-700 bg-purple-100 border border-purple-300';
      default: return 'text-gray-700 bg-gray-100 border border-gray-300';
    }
  };

  return (
    <div className="space-y-8">
      {/* Category Selection - Enhanced colors */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories?.map((category) => (
          <div
            key={category?.id}
            onClick={() => setSelectedCategory(category?.id)}
            className={`
              p-6 rounded-xl border-2 cursor-pointer transition-all duration-300 hover:shadow-lg
              ${selectedCategory === category?.id
                ? 'border-blue-500 bg-blue-50 shadow-md transform scale-105'
                : 'border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-25'
              }
            `}
          >
            <div className="flex items-center space-x-3 mb-4">
              <span className="text-3xl">{category?.icon}</span>
              <h3 className="text-lg font-semibold text-gray-900">{category?.name}</h3>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-700 font-medium">Questions:</span>
                <span className="font-semibold text-gray-900">{category?.questions}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-700 font-medium">Difficulty:</span>
                <span className={`px-2 py-1 rounded text-xs font-semibold ${getDifficultyColor(category?.difficulty)}`}>
                  {category?.difficulty}
                </span>
              </div>
            </div>

            {selectedCategory === category?.id && (
              <div className="mt-4 space-y-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  fullWidth
                  onClick={startTimedPractice}
                  className="font-semibold"
                >
                  Start Timed Practice
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  fullWidth
                  className="font-semibold"
                >
                  Practice Mode
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
      
      {/* Question Interface - Improved contrast */}
      {isTimerActive && (
        <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
          {/* Timer and Progress */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-700 font-medium">
                Question {currentQuestion + 1} of {sampleQuestions?.length}
              </div>
              <div className={`font-mono text-lg font-bold ${timeRemaining < 60 ? 'text-red-600' : 'text-blue-600'}`}>
                {formatTime(timeRemaining)}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-600 font-medium">Difficulty:</span>
              <span className={`px-2 py-1 rounded text-xs font-semibold ${getDifficultyColor(sampleQuestions?.[currentQuestion]?.difficulty)}`}>
                {sampleQuestions?.[currentQuestion]?.difficulty}
              </span>
            </div>
          </div>

          {/* Progress Bar - Enhanced colors */}
          <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentQuestion + 1) / sampleQuestions?.length) * 100}%` }}
            />
          </div>

          {/* Question - Better text contrast */}
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              {sampleQuestions?.[currentQuestion]?.question}
            </h3>

            <div className="space-y-3">
              {sampleQuestions?.[currentQuestion]?.options?.map((option, index) => (
                <label
                  key={index}
                  className={`
                    flex items-center space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all duration-200
                    ${selectedAnswer === index
                      ? showExplanation
                        ? index === sampleQuestions?.[currentQuestion]?.correctAnswer
                          ? 'border-green-500 bg-green-50 text-green-900' :'border-red-500 bg-red-50 text-red-900' :'border-blue-500 bg-blue-50 text-blue-900' :'border-gray-300 hover:border-gray-400 bg-white text-gray-900'
                    }
                    ${showExplanation && index === sampleQuestions?.[currentQuestion]?.correctAnswer
                      ? 'border-green-500 bg-green-50 text-green-900' :''
                    }
                  `}
                >
                  <input
                    type="radio"
                    name="answer"
                    value={index}
                    checked={selectedAnswer === index}
                    onChange={() => handleAnswerSelect(index)}
                    disabled={showExplanation}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="font-medium">{option}</span>
                  {showExplanation && index === sampleQuestions?.[currentQuestion]?.correctAnswer && (
                    <span className="ml-auto text-green-600 font-bold">✓</span>
                  )}
                  {showExplanation && selectedAnswer === index && index !== sampleQuestions?.[currentQuestion]?.correctAnswer && (
                    <span className="ml-auto text-red-600 font-bold">✗</span>
                  )}
                </label>
              ))}
            </div>
          </div>

          {/* Explanation - Enhanced styling */}
          {showExplanation && (
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
              <h4 className="font-semibold text-blue-900 mb-2">Explanation:</h4>
              <p className="text-blue-800">{sampleQuestions?.[currentQuestion]?.explanation}</p>
            </div>
          )}

          {/* Action Buttons - Keep existing */}
          <div className="flex justify-between">
            <Button variant="outline" disabled>
              Previous
            </Button>
            
            {!showExplanation ? (
              <Button 
                onClick={handleSubmitAnswer}
                disabled={selectedAnswer === ''}
              >
                Submit Answer
              </Button>
            ) : (
              <Button onClick={handleNextQuestion}>
                Next Question
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Performance Summary - Enhanced colors */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
        <h3 className="text-xl font-semibold mb-4 text-gray-900">Your MCQ Performance</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="text-2xl font-bold text-green-700">78%</div>
            <div className="text-sm text-green-800 font-medium">Overall Accuracy</div>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="text-2xl font-bold text-blue-700">145</div>
            <div className="text-sm text-blue-800 font-medium">Questions Completed</div>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
            <div className="text-2xl font-bold text-purple-700">2.3min</div>
            <div className="text-sm text-purple-800 font-medium">Avg. Time per Question</div>
          </div>
        </div>

        <div className="mt-6">
          <h4 className="font-semibold mb-3 text-gray-900">Weak Areas to Focus:</h4>
          <div className="flex flex-wrap gap-2">
            {userProgress?.weakAreas?.map((area, index) => (
              <span key={index} className="px-3 py-1 bg-red-100 text-red-800 border border-red-300 rounded-full text-sm font-medium">
                {area}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MCQSection;